// ============================================
// SERVICIOSDELDIA.JS — Vista de eventos del día agrupados por técnico
// ============================================

import React, { useEffect, useState } from 'react';
import ExpedienteDetalle from './components/ExpedienteDetalle';
import {
  API_BASE_EXP, PP_EXP, COL_EXP,
  estiloBotonExp, estiloBotonSecExp, estiloTarjetaExp,
} from './components/helpersExpedientes';

export default function ServiciosDelDia({ esCelular }) {
  const [eventos, setEventos] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const hoy = new Date();
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).toISOString();
      const fin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1).toISOString();
      const params = new URLSearchParams({ desde: inicio, hasta: fin });
      const [eR, uR] = await Promise.all([
        fetch(`${API_BASE_EXP}/api/expedientes/eventos/calendario?${params}`).then(r => r.json()),
        (async () => {
          const token = localStorage.getItem('beep_token');
          const h = token ? { Authorization: `Bearer ${token}` } : {};
          try { return await fetch(`${API_BASE_EXP}/api/usuarios`, { headers: h }).then(r => r.json()); }
          catch { return { usuarios: [] }; }
        })(),
      ]);
      setEventos(eR.eventos || []);
      setTecnicos(uR.usuarios || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  if (detalle) {
    return <ExpedienteDetalle id={detalle} onVolver={() => { setDetalle(null); cargar(); }} />;
  }

  // Agrupar por técnico
  const porTecnico = {};
  eventos.forEach(ev => {
    const tid = ev.tecnico_id || 'sin_asignar';
    if (!porTecnico[tid]) porTecnico[tid] = [];
    porTecnico[tid].push(ev);
  });
  const tecnicoNombre = (id) => {
    if (id === 'sin_asignar') return '— Sin técnico asignado —';
    const u = tecnicos.find(t => String(t.id) === String(id));
    return u ? u.nombre : `Técnico #${id}`;
  };

  const hoyStr = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ fontFamily: PP_EXP, background: COL_EXP.fondo, minHeight: '100%', color: COL_EXP.gris }}>
      <div style={{
        padding: esCelular ? 14 : '18px 24px', borderBottom: `1px solid ${COL_EXP.borde}`, background: '#fff',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: COL_EXP.oscuro }}>🛠️ Servicios del día</div>
        <div style={{ fontSize: 12, color: COL_EXP.grisClaro, marginTop: 2, textTransform: 'capitalize' }}>
          {hoyStr} · {eventos.length} evento{eventos.length === 1 ? '' : 's'}
        </div>
      </div>

      <div style={{ padding: esCelular ? 12 : 20, display: 'grid', gap: 14 }}>
        {cargando && <div style={{ textAlign: 'center', padding: 40, color: COL_EXP.grisClaro }}>Cargando…</div>}
        {!cargando && eventos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: COL_EXP.grisClaro, fontSize: 13 }}>
            No hay eventos programados para hoy.
          </div>
        )}
        {Object.entries(porTecnico).map(([tid, list]) => (
          <div key={tid} style={estiloTarjetaExp}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 10 }}>
              👤 {tecnicoNombre(tid)} · {list.length}
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {list.map(ev => (
                <EventoFila key={ev.id} evento={ev} onAbrir={() => setDetalle(ev.expediente_id)} onActualizar={cargar} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventoFila({ evento, onAbrir, onActualizar }) {
  const hora = new Date(evento.fecha_inicio).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const color = evento.estado === 'completado' ? '#1D9E75' : evento.estado === 'en_curso' ? '#f59e0b' : COL_EXP.principal;

  const iniciar = async () => {
    const payload = {};
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
        payload.gps_lat = pos.coords.latitude;
        payload.gps_lng = pos.coords.longitude;
      } catch { /* ok sin gps */ }
    }
    await fetch(`${API_BASE_EXP}/api/expedientes/eventos/${evento.id}/iniciar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    onActualizar();
  };

  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px',
      background: COL_EXP.fondo, borderRadius: 8, cursor: 'pointer',
      borderLeft: `3px solid ${evento.tipo_color || COL_EXP.principal}`,
    }} onClick={onAbrir}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, minWidth: 54 }}>{hora}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COL_EXP.oscuro }}>
          {evento.tipo_icono} {evento.expediente_titulo}
        </div>
        <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>
          {evento.folio} · {evento.tipo_evento}
          {evento.sucursal_nombre && ` · 📍 ${evento.sucursal_nombre}`}
        </div>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: color + '20', color, textTransform: 'uppercase' }}>
        {evento.estado}
      </span>
      {evento.estado === 'programado' && (
        <button onClick={(e) => { e.stopPropagation(); iniciar(); }} style={{ ...estiloBotonExp, padding: '4px 10px', fontSize: 10 }}>▶ Iniciar</button>
      )}
    </div>
  );
}
